import { useState, useEffect } from "react";
import api from "../../../Axios.js";
import { FaCheckCircle } from "react-icons/fa";
import { defaultDateFormatter, modalStyle, modalContentStyle } from "../../../Helper/functionHelper.js";
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import useAuthUser from "../../../hooks/useAuthUser.js";
import { useNavigate } from "react-router-dom";
import { CustomSelect } from "../../../Helper/functionHelper.js";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, parseISO } from "date-fns";
import { TextField } from "@mui/material";
import ExpandableText from "../../../components/common/ExpandableText.jsx";

// Mirrors Services/assetApproval.service.js on the backend (source of truth for gating) —
// used here only to decide which action buttons a row should show; the backend re-validates
// every action independently, so this is a UX convenience, not a security boundary.
const LEVEL_STATUS = {
    reporting_manager: { approve: 1, reject: 2, sendback: 10 },
    vertical_head: { approve: 14, reject: 15, sendback: 11 },
    it_procurement: { approve: 16, reject: 17, sendback: 12 },
    finance_head: { approve: 18, reject: 19, sendback: 13 },
    business_head: { approve: 20, reject: 21, sendback: 22 },
};
// After Business Head approves, IT Procurement always gets a second checkpoint to decide
// whether this request also needs Finance Head — these two codes represent that decision.
const IT_FINANCE_GATE_STATUS = {
    routeToFinance: 23,
    finalize: 24,
};
// After Finance Head approves — whether reached directly or via the Business Head gate above —
// IT Procurement always gets one final approve/reject before the request is ready for assignment.
const IT_FINAL_REVIEW_STATUS = {
    approve: 25,
    reject: 26,
};
const LEVEL_ORDER = ['reporting_manager', 'vertical_head', 'it_procurement'];

// Business Head and Finance Head are never asset-type-configured levels — IT Procurement picks
// one of 4 routings per request (Business Head only / Finance Head only / both, sequentially / or
// neither) when they submit their estimate. Mirrors nextDynamicLevel on the backend.
const DYNAMIC_LEVELS = ['business_head', 'finance_head'];
const nextDynamicLevelFor = (afterLevel, requestFlags = {}) => {
    const enabled = {
        business_head: Boolean(requestFlags.send_to_business_head),
        finance_head: Boolean(requestFlags.send_to_finance),
    };
    const startIdx = afterLevel === 'it_procurement' ? 0 : DYNAMIC_LEVELS.indexOf(afterLevel) + 1;
    for (let i = startIdx; i < DYNAMIC_LEVELS.length; i++) {
        if (enabled[DYNAMIC_LEVELS[i]]) return DYNAMIC_LEVELS[i];
    }
    return null;
};

const activeLevelsFor = (assetType) => {
    const configured = Array.isArray(assetType?.approval_flow) ? assetType.approval_flow : [];
    return LEVEL_ORDER.filter(l => configured.includes(l));
};
const nextLevelFor = (level, levels) => {
    const idx = levels.indexOf(level);
    return (idx === -1 || idx === levels.length - 1) ? null : levels[idx + 1];
};
const resolveAwaitingLevel = (status, levels, requestFlags = {}) => {
    if (status === 0) return levels.length ? levels[0] : null;
    if (status === LEVEL_STATUS.reporting_manager.sendback) return 'employee';
    if (status === LEVEL_STATUS.it_procurement.sendback) return 'employee';
    if (status === LEVEL_STATUS.vertical_head.sendback) return 'reporting_manager';
    if (status === LEVEL_STATUS.finance_head.sendback) return 'it_procurement';
    if (status === LEVEL_STATUS.business_head.sendback) return 'it_procurement';
    if (status === LEVEL_STATUS.reporting_manager.approve) return nextLevelFor('reporting_manager', levels);
    if (status === LEVEL_STATUS.vertical_head.approve) return nextLevelFor('vertical_head', levels);
    if (status === LEVEL_STATUS.it_procurement.approve) return nextDynamicLevelFor('it_procurement', requestFlags);
    // Business Head approving always hands control back to IT Procurement, regardless of what
    // was chosen upfront — IT decides at that point whether Finance Head is needed too.
    if (status === LEVEL_STATUS.business_head.approve) return 'it_procurement';
    if (status === IT_FINANCE_GATE_STATUS.routeToFinance) return 'finance_head';
    if (status === IT_FINANCE_GATE_STATUS.finalize) return null;
    // Finance Head approving — direct or via the gate above — always hands back to IT for a
    // final approve/reject, never straight to assignment.
    if (status === LEVEL_STATUS.finance_head.approve) return 'it_procurement';
    if (status === IT_FINAL_REVIEW_STATUS.approve) return null;
    if (status === IT_FINAL_REVIEW_STATUS.reject) return null;
    return null;
};

const RequestTab = () => {
    const navigate = useNavigate();

    const [declaration, setDeclaration] = useState('');
    const [actionDeclaration, setActionDeclaration] = useState('');
    const [enableButton, setEnableButton] = useState(true);
    const fetchDeclaration = async () => {
        try {
            const res = await api.get('/declaration');
            setDeclaration(res.data.data?.declaration || '');
        } catch (err) {
            console.error(err);
        }
    };
    useEffect(() => {
        fetchDeclaration();
    }, []);
    const checkboxChange = (e) => {
        if (e.target.checked) {
            setEnableButton(true);
        } else {
            setEnableButton(false);
        }
    };

    const { loggedInUser } = useAuthUser();
    const userRole = Number(loggedInUser?.role);

    const [itemsPerPage, setItemsPerPage] = useState(10);
    // assign/return/replace

    const [assignModalShow, setAssignModalShow] = useState(false);
    const [assignModalForm, setAssignModalForm] = useState({
        request_id: null,
        asset_type: null,
        user_id: '',
        request_type: '',
        asset_id: null,
        new_asset_id: null,
        remark: '',
    });

    const [errorsAssignForm, setErrorsAssignForm] = useState({});
    const handleAssignChange = (e) => {
        const { name, value } = e.target;
        setErrorsAssignForm({ ...errorsAssignForm, [name]: '' });
        setAssignModalForm({ ...assignModalForm, [name]: value });
    };
    const [assignmentLables, setAssignmentLables] = useState({
        labelTitle: 'Assign Asset',
        labelUser: 'Assign to*',
        userDisabled: true,
        assetDisabled: false
    })

    useEffect(() => {
        if (assignModalForm.request_type === 'New') {
            setAssignmentLables({
                labelTitle: 'Assign Asset',
                labelUser: 'Assign to*',
                userDisabled: true,
                assetDisabled: false
            });
        } else if (assignModalForm.request_type === 'Return') {
            setAssignmentLables({
                labelTitle: 'Return Asset',
                labelUser: 'Return From*',
                userDisabled: true,
                assetDisabled: true
            });
        } else if (assignModalForm.request_type === 'Replace') {
            setAssignmentLables({
                labelTitle: 'Replace Asset',
                labelUser: 'Replace From*',
                userDisabled: true,
                assetDisabled: true
            });
        } else {
            // console.log('Invalid request type');
            // toast.error('Invalid request type', {
            //     position: "top-right",
            //     autoClose: 3000,
            // });
        }
    }, [assignModalForm]);

    const [isAssigning, setIsAssigning] = useState(false);

    const handleAssignClicked = async (e) => {
        e.preventDefault();

        if (isAssigning) return; // prevent multiple clicks

        try {
            setIsAssigning(true);

            // validate errors here
            const newErrors = {};

            if (!assignModalForm.request_id) newErrors.request_id = 'Request id is required';
            if (!assignModalForm.request_type) newErrors.request_type = 'Request Type is required';
            if (!assignModalForm.asset_id) newErrors.asset_id = 'Asset is required';
            if (!assignModalForm.user_id) newErrors.user_id = 'User is required';
            if (!assignModalForm.remark.trim()) newErrors.remark = 'Remark is required';

            if (assignModalForm.request_type && assignModalForm.request_type === "Replace") {
                if (!assignModalForm.new_asset_id) newErrors.new_asset_id = 'New Asset is required';
            }

            if (assignModalForm.asset_id === assignModalForm.new_asset_id) {
                newErrors.new_asset_id = 'New Asset can\'t be same as old asset.';
            }

            setErrorsAssignForm(newErrors);

            // console.log(assignModalForm); return false;


            if (Object.keys(newErrors).length === 0) {

                const result = await api.post(`/assets/assignment`, assignModalForm);
                toast.success(result?.data?.message || "Assignment updated!");

                setAssignModalShow(false);
                getRequests();
            } else {
                console.log('Errors:', newErrors);
            }
        } catch (err) {
            toast.error(err.response?.data?.error || 'An error occurred. Please try again.', {
                position: "top-right",
                autoClose: 3000,
            });
        } finally {
            setIsAssigning(false);
        }
    }



    const [requests, setRequests] = useState([]);
    const [assetTypes, setAssetTypes] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [users, setUsers] = useState([]);
    const [managers, setManagers] = useState([]);
    const [assetAssignments, setAssetAssignments] = useState([]);

    const [errorsForm, setErrorsForm] = useState({});

    const [ActionVisible, setActionVisible] = useState(false);
    const [actionTitle, setActionTitle] = useState('');
    const [ActionData, setActionData] = useState(null);
    const [actionFormData, setActionFormData] = useState({
        _id: null,
        status: '',
        remark: '',
    });
    // Vertical Head approval asks for a business justification instead of a plain remark.
    const remarkLabel = Number(actionFormData.status) === LEVEL_STATUS.vertical_head.approve
        ? 'Business Justification (Remark)'
        : 'Remark';

    useEffect(() => {
        if (ActionData) {
            setActionFormData({
                _id: ActionData?._id || null,
                status: ActionData?.status || 0,
                remark: ActionData?.remark || '',
            });
            setActionVisible(true);
        }
    }, [ActionData]);

    const [modalVisible, setModalVisible] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [requestFormData, setRequestFormData] = useState({
        request_type: '',
        assignment_id: null,
        asset_type: null,
        request_remark: '',
        status: 0,
        _id: null,
    });

    useEffect(() => {
        if (modalData) {
            setRequestFormData({
                request_type: modalData.request_type || '',
                assignment_id: modalData.old_asset_assignment_id || '',
                asset_type: modalData?.asset_type?._id || '',
                request_remark: modalData?.request_remark || '',
                status: modalData.status ?? 0,
                _id: modalData._id || null,
            });
            setModalVisible(true);
        }
    }, [modalData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setErrorsForm({ ...errorsForm, [name]: '' });

        setRequestFormData((prevData) => {
            let updatedData = { ...prevData, [name]: value };
            // Conditionally clear other fields based on request_type
            if (name === "request_type") {
                if (value === "New") {
                    updatedData.assignment_id = null;
                    updatedData.asset_type = "";
                } else if (value === "Return" || value === "Replace") {
                    updatedData.asset_type = "";
                    updatedData.assignment_id = "";
                }
            }
            return updatedData;
        });
    };

    const handleChangeAction = (e) => {
        const { name, value } = e.target;
        setErrorsForm({ ...errorsForm, [name]: '' });

        setActionFormData((prevData) => {
            let updatedData = { ...prevData, [name]: value };
            // Conditionally clear other fields based on request_type
            return updatedData;
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();

        if (isAssigning) return; // prevent multiple clicks

        try {
            setIsAssigning(true);

            // validate errors here
            const newErrors = {};

            if (!requestFormData.request_type?.trim()) {
                newErrors.request_type = 'Request type is required';
            } else {
                // Conditional validation based on request_type
                if (requestFormData.request_type === 'New') {
                    if (!requestFormData.asset_type) {
                        newErrors.asset_type = 'Asset type is required';
                    }
                } else if (requestFormData.request_type === 'Return' || requestFormData.request_type === 'Replace') {
                    if (!requestFormData.assignment_id) {
                        newErrors.assignment_id = 'Assigned asset is required';
                    }
                }
            }

            if (!requestFormData.request_remark?.trim()) {
                newErrors.request_remark = 'Description is required';
            }

            setErrorsForm(newErrors);
            if (Object.keys(newErrors).length === 0) {

                if (requestFormData._id) {
                    await api.patch(`/asset-requests/${requestFormData._id}`, requestFormData);
                    toast.success("Request updated!");
                } else {
                    await api.post(`/asset-requests`, requestFormData);
                    toast.success("Request created!");
                }
                setModalVisible(false);
                getRequests();
            }
        } catch (err) {
            toast.error(err.response?.data?.error || 'An error occurred. Please try again.', {
                position: "top-right",
                autoClose: 3000,
            });
        } finally {
            setIsAssigning(false);
        }
    };

    const handleActionSave = async (e) => {
        e.preventDefault();

        if (isAssigning) return; // prevent multiple clicks

        try {
            setIsAssigning(true);
            // validate errors here
            const newErrors = {};
            if (!actionFormData.remark?.trim()) {
                newErrors.remark = `${remarkLabel} is required`;
                // if(![1,3].includes(actionFormData.status)) {
                //     newErrors.remark = 'Remark is required';
                // } else {
                //     actionFormData.remark = 'Approved';
                // }
            }
            setErrorsForm(newErrors);
            if (Object.keys(newErrors).length === 0) {
                const result = await api.put(`/asset-requests/remark`, actionFormData);
                toast.success(result?.data?.message || 'Remark updated!');
                setActionVisible(false);
                getRequests();
            }
        } catch (err) {
            toast.error(err.response?.data?.error || 'An error occurred. Please try again.', {
                position: "top-right",
                autoClose: 3000,
            });
        } finally {
            setIsAssigning(false);
        }
    };


    // IT Procurement's routing choice when they submit their estimate (the "Approve" action at
    // that level): both are optional and mutually exclusive — checking one unchecks the other.
    // Leaving both unchecked means IT approves the request directly themselves; checking either
    // makes that approval a required next step before the request can proceed.
    const routingFor = (request) => {
        if (request?.send_to_business_head) return 'business_head';
        if (request?.send_to_finance) return 'finance_head';
        return 'none';
    };

    const [costModalShow, setCostModalShow] = useState(false);
    const [costModalForm, setCostModalForm] = useState({
        _id: null,
        mode: 'submit', // 'submit' (first checkpoint) | 'financeGate' (second, post-Business-Head checkpoint)
        routing: 'none',
        estimated_cost: '',
        vendor_name: '',
        lead_time: '',
        procurement_notes: '',
        remark: '',
        attachment: null,
    });
    const [errorsCostForm, setErrorsCostForm] = useState({});
    const handleCostChange = (e) => {
        const { name, value } = e.target;
        setErrorsCostForm({ ...errorsCostForm, [name]: '' });
        setCostModalForm((prev) => ({ ...prev, [name]: value }));
    };
    const handleCostRoutingCheckbox = (value, checked) => {
        setErrorsCostForm({});
        setCostModalForm((prev) => ({ ...prev, routing: checked ? value : 'none' }));
    };
    const handleCostAttachmentChange = (e) => {
        setCostModalForm((prev) => ({ ...prev, attachment: e.target.files?.[0] || null }));
    };
    const handleCostSave = async (e) => {
        e.preventDefault();
        if (isAssigning) return; // prevent multiple clicks
        try {
            setIsAssigning(true);
            const routingBeyondIT = costModalForm.routing !== 'none';
            const isFinanceRouting = costModalForm.routing === 'finance_head';
            const isBusinessHeadRouting = costModalForm.routing === 'business_head';
            const newErrors = {};
            if (isFinanceRouting) {
                if (!costModalForm.estimated_cost) newErrors.estimated_cost = 'Estimated cost is required';
            } else if (!routingBeyondIT) {
                if (!costModalForm.remark?.trim()) newErrors.remark = 'Remark is required';
            }
            // Business Head routing has nothing required — the attachment is optional.
            setErrorsCostForm(newErrors);
            if (Object.keys(newErrors).length === 0) {
                const status = costModalForm.mode === 'financeGate'
                    ? (routingBeyondIT ? IT_FINANCE_GATE_STATUS.routeToFinance : IT_FINANCE_GATE_STATUS.finalize)
                    : LEVEL_STATUS.it_procurement.approve;

                const formData = new FormData();
                formData.append('_id', costModalForm._id);
                formData.append('status', status);
                formData.append('send_to_business_head', ['business_head', 'both'].includes(costModalForm.routing));
                formData.append('send_to_finance', ['finance_head', 'both'].includes(costModalForm.routing));
                if (isFinanceRouting) {
                    formData.append('estimated_cost', costModalForm.estimated_cost);
                    formData.append('vendor_name', costModalForm.vendor_name || '');
                    formData.append('lead_time', costModalForm.lead_time);
                    formData.append('procurement_notes', costModalForm.procurement_notes || '');
                    formData.append('remark', costModalForm.procurement_notes?.trim() || 'Cost estimate submitted');
                } else if (isBusinessHeadRouting) {
                    formData.append('remark', 'Routed to Business Head for approval');
                    if (costModalForm.attachment) formData.append('attachment', costModalForm.attachment);
                } else {
                    formData.append('remark', costModalForm.remark.trim());
                }

                const result = await api.put('/asset-requests/remark', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                toast.success(result?.data?.message || 'Cost estimate submitted!');
                setCostModalShow(false);
                getRequests();
            }
        } catch (err) {
            toast.error(err.response?.data?.error || 'An error occurred. Please try again.', {
                position: "top-right",
                autoClose: 3000,
            });
        } finally {
            setIsAssigning(false);
        }
    };

    const statuses = [
        {
            value: 0,
            label: 'Requested by employee',
            class: 'text-warning'
        },
        {
            value: 1,
            label: 'Approved by manager',
            class: 'text-success'
        },
        {
            value: 2,
            label: 'Rejected by manager',
            class: 'text-danger'
        },
        {
            value: 3,
            label: 'Approved by admin',
            class: 'text-success'
        },
        {
            value: 4,
            label: 'Rejected by admin',
            class: 'text-danger'
        },
        {
            value: 5,
            label: 'Assigned by Admin',
            class: 'text-success'
        },
        {
            value: 6,
            label: 'Acknowledged by employee',
            class: 'text-success'
        },
        {
            value: 7,
            label: 'Rejected by employee',
            class: 'text-danger'
        },
        {
            value: 8,
            label: 'Cancelled by employee',
            class: 'text-danger'
        },
        {
            value: 9,
            label: 'Return accepted by Admin',
            class: 'text-success'
        },
        {
            value: 10,
            label: 'Sent back by Reporting Manager for clarification',
            class: 'text-warning'
        },
        {
            value: 11,
            label: 'Sent back by Vertical Head for clarification',
            class: 'text-warning'
        },
        {
            value: 12,
            label: 'Sent back by IT Procurement for clarification',
            class: 'text-warning'
        },
        {
            value: 13,
            label: 'Sent back by Finance Head for clarification',
            class: 'text-warning'
        },
        {
            value: 14,
            label: 'Approved by Vertical Head',
            class: 'text-success'
        },
        {
            value: 15,
            label: 'Rejected by Vertical Head',
            class: 'text-danger'
        },
        {
            value: 16,
            label: 'Approved by IT Procurement',
            class: 'text-success'
        },
        {
            value: 17,
            label: 'Rejected by IT Procurement',
            class: 'text-danger'
        },
        {
            value: 18,
            label: 'Approved by Finance Head',
            class: 'text-success'
        },
        {
            value: 19,
            label: 'Rejected by Finance Head',
            class: 'text-danger'
        },
        {
            value: 20,
            label: 'Approved by Business Head',
            class: 'text-success'
        },
        {
            value: 21,
            label: 'Rejected by Business Head',
            class: 'text-danger'
        },
        {
            value: 22,
            label: 'Sent back by Business Head for clarification',
            class: 'text-warning'
        },
        {
            value: 23,
            label: 'Forwarded to Finance Head by IT Procurement',
            class: 'text-warning'
        },
        {
            value: 24,
            label: 'Finalized by IT Procurement',
            class: 'text-success'
        },
        {
            value: 25,
            label: 'Approved by IT Procurement (Final Review)',
            class: 'text-success'
        },
        {
            value: 26,
            label: 'Rejected by IT Procurement (Final Review)',
            class: 'text-danger'
        }
    ];

    // "Approved by IT Procurement" (status 16) or "Approved by Business Head" (status 20) reads
    // like the request is fully done, even while it's actually sitting with Business Head/Finance
    // Head/IT's second checkpoint — since none of those are distinct status codes, show who it's
    // actually pending with instead of the raw status label whenever that's the case. Once that
    // level itself acts, its own status code (18/19/13/21/22/24/etc.) is specific enough already.
    const getCurrentStatusDisplay = (request) => {
        const awaitingLevel = resolveAwaitingLevel(
            request.status,
            activeLevelsFor(request.asset_type),
            { send_to_finance: request.send_to_finance, send_to_business_head: request.send_to_business_head }
        );
        if (awaitingLevel === 'business_head') return { label: 'Pending Business Head Approval', class: 'text-warning' };
        if (awaitingLevel === 'finance_head') return { label: 'Pending Finance Head Approval', class: 'text-warning' };
        if (awaitingLevel === 'it_procurement' && request.status === LEVEL_STATUS.business_head.approve) {
            return { label: 'Pending IT Procurement Review', class: 'text-warning' };
        }
        if (awaitingLevel === 'it_procurement' && request.status === LEVEL_STATUS.finance_head.approve) {
            return { label: 'Pending IT Procurement Final Review', class: 'text-warning' };
        }
        const found = statuses.find(s => s.value === request.status);
        return { label: found?.label || 'Unknown', class: found?.class || 'text-secondary' };
    };

    const getRequests = async () => {
        try {
            const res = await api.get('/asset-requests');
            setRequests(res.data.data);
        } catch (err) {
            console.error(err);
        }
    }
    const fetchAssetTypes = async () => {
        try {
            const res = await api.get('/asset-types');
            setAssetTypes(res.data.data);
        } catch (err) {
            console.error(err);
        }
    };
    const fetchAssignedAssets = async () => {
        try {
            const res = await api.get('/assets?status=1');
            setAssetAssignments(res.data.data);
        } catch (err) {
            console.error(err);
        }
    };
    const fetchDepartments = async (params) => {
        try {
            const res = await api.get('/departments', { params: params });
            setDepartments(res.data.data);
        } catch (err) {
            console.error(err)
        }
    }
    const fetchUsers = async (params) => {
        try {
            const res = await api.get('/users', { params: params });
            setUsers(res.data.data);

            // fetch managers
            let ress = '';
            if ([1, 2, 8, 9].includes(userRole)) { // if admin or IT Admin
                ress = await api.get('/users/get-reporting-managers', { params: params });
            } else {
                ress = await api.get('/users/my-reportee-managers');
            }
            setManagers(ress.data.data);
        } catch (err) {
            console.error(err)
        }
    }
    useEffect(() => {
        getRequests();
        fetchAssetTypes();
        fetchAssignedAssets();
        fetchDepartments({ _DeptStatus: 1 });
        fetchUsers({ _UserStatus: 1 });
    }, []);

    const [filterForm, setFilterForm] = useState({
        search_input: '',
        _UserEmployeeID: '',
        department: '',
        rep_manager: '',
        status: '',
        asset_type: '',
        request_type: '',
        from_date: '',
        to_date: '',
        employee_status: '',
    });
    const handleChangeFilter = (e) => {
        const { name, value } = e.target;
        setFilterForm({ ...filterForm, [name]: value });
    };

    const [filteredRequests, setFilteredRequests] = useState([]);

    useEffect(() => {
        const result = requests.filter(item => {
            const search_fields = [
                item?.request_remark,
                item?.user_details?.fullname,
                item?.user_details?._UserEmployeeID,
                item?.asset?.old?.asset_name,
                item?.asset?.old?.sr_no,
                item?.asset?.old?.description,
                item?.asset?.new?.asset_name,
                item?.asset?.new?.sr_no,
                item?.asset?.new?.description
            ]
            .filter(Boolean)       // remove null/undefined/empty
            .join(" ")             // join with space
            .toLowerCase();

            const department = item?.user_details?._UserDepartment || '';
            const rep_manager = item?.user_details?._ReportingManager || '';
            const status = item.status;
            const asset_type = item?.asset_type?._id || item?.asset?.old?.asset_type?._id || '';
            const request_type = item?.request_type || '';
            const employee_status = item?.user_details?._UserStatus || 0;

            const matchesSearched = !filterForm.search_input || search_fields.includes(filterForm.search_input.toLowerCase());

            const matchesDepartment = !filterForm.department || department === Number(filterForm.department);
            const matchesRepManager = !filterForm.rep_manager || rep_manager === Number(filterForm.rep_manager);
            const matchesStatus = !filterForm.status || status === Number(filterForm.status);
            const matchesAssetType = !filterForm.asset_type || asset_type === filterForm.asset_type;
            const matchesRequestType = !filterForm.request_type || request_type.toLowerCase().includes(filterForm.request_type.toLowerCase());
            const matchesEmployeeStatus = !filterForm.employee_status || employee_status === Number(filterForm.employee_status);

            const requestDate = item?.created_at ? format(parseISO(item.created_at), 'yyyy-MM-dd') : null;
            const from = filterForm.from_date || null;
            const to = filterForm.to_date || null;
            // Date Range Filter
            const matchesDateRange = (!from || !to) || (requestDate && requestDate >= from && requestDate <= to);

            return matchesSearched && matchesDepartment && matchesRepManager && matchesStatus && matchesAssetType && matchesRequestType && matchesEmployeeStatus && matchesDateRange;
        });

        setFilteredRequests(result);
    }, [filterForm, requests]);


    const exportToExcel = () => {
        const exportData = filteredRequests.map(item => ({
            "Employee": item.user_details.fullname,
            "Reporting manager": item.user_details.reporting_manager_name,
            "Department": item.user_details.department,
            "Designation": item.user_details.designation,
            "Request Type": item.request_type,
            "Asset Type": item?.asset_type?.type_name || item?.asset?.old?.asset_type?.type_name || '-',
            "Remark": item.request_remark,
            "Status": getCurrentStatusDisplay(item).label,
            "Created By": item.created_by_name,
            "Created At": defaultDateFormatter(item.created_at),
            "Updated By": item.updated_by_name,
            "Updated At": defaultDateFormatter(item.updated_at),
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "AssetRequests");

        XLSX.writeFile(workbook, `Asset Requests Exported - ${new Date().toLocaleDateString()}.xlsx`);
    };

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentData = filteredRequests.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // useEffect(() => {
    //     console.log('Token: ', localStorage.getItem('token'))
    // }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [filterForm, itemsPerPage]);



    return (
        <div>

            {/* assign asset modal */}
            {assignModalShow && (
                <div className="modal show d-block fade" tabIndex="-1" role="dialog" style={modalStyle} onClick={() => setAssignModalShow(false)}>
                    <div className="modal-dialog" role="document" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title"> {assignmentLables.labelTitle} </h5>
                                <button data-bs-toggle="tooltip" data-bs-placement="right" title="Close"
                                    type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={(e) => setAssignModalShow(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="popup-form">
                                    <div className="popup-form-row form-row-full">
                                        <div className="row">
                                            <div className="form-info">
                                                <div className="form-info-label"> {assignmentLables.labelUser}</div>
                                                <div className="form-info-content">
                                                    <CustomSelect
                                                        name="user_id"
                                                        value={assignModalForm.user_id}
                                                        options={[
                                                            { value: '', label: '-- Select --' },
                                                            ...users.map(user => ({
                                                                value: user._UserID,
                                                                label: [
                                                                    user._FirstName,
                                                                    user._MiddleName,
                                                                    user._LastName
                                                                ]
                                                                    .filter(Boolean)
                                                                    .join(' ') + ' (' + user._UserEmployeeID + ')'
                                                            }))
                                                        ]}
                                                        onChange={handleAssignChange}
                                                        disabled={assignmentLables.userDisabled}
                                                    />
                                                    {errorsAssignForm.user_id && <div className="text-danger">{errorsAssignForm.user_id}</div>}
                                                </div>
                                            </div>
                                        </div>
                                        {assignModalForm.request_type === "New" && (
                                            <div className="row">
                                                <div className="form-info">
                                                    <div className="form-info-label">Asset</div>
                                                    <div className="form-info-content">
                                                        <CustomSelect
                                                            name="asset_id"
                                                            value={assignModalForm.asset_id}
                                                            options={[
                                                                { value: '', label: '-- Select --' },
                                                                ...assetAssignments
                                                                    .filter(asset => asset.asset_type?._id === assignModalForm?.asset_type && asset.assigned_to === null)
                                                                    .map(asset => ({
                                                                        value: asset._id,
                                                                        label: asset.asset_name + '(' + asset.sr_no + ')'
                                                                    }))
                                                            ]}
                                                            onChange={handleAssignChange}
                                                            disabled={assignmentLables.assetDisabled}
                                                        />
                                                        {errorsAssignForm.asset_id && <div className="text-danger">{errorsAssignForm.asset_id}</div>}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {(assignModalForm.request_type === "Return" || assignModalForm.request_type === "Replace") && (
                                            <div className="row">
                                                <div className="form-info">
                                                    <div className="form-info-label">Asset</div>
                                                    <div className="form-info-content">
                                                        <CustomSelect
                                                            name="asset_id"
                                                            value={assignModalForm.asset_id}
                                                            options={[
                                                                { value: '', label: '-- Select --' },
                                                                ...assetAssignments
                                                                    .filter(asset => asset.asset_type?._id === assignModalForm?.asset_type)
                                                                    .map(asset => ({
                                                                        value: asset._id,
                                                                        label: asset.asset_name + '(' + asset.sr_no + ')'
                                                                    }))
                                                            ]}
                                                            onChange={handleAssignChange}
                                                            disabled={assignmentLables.assetDisabled}
                                                        />
                                                        {errorsAssignForm.asset_id && <div className="text-danger">{errorsAssignForm.asset_id}</div>}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {assignModalForm.request_type === "Replace" && (
                                            <div className="row">
                                                <div className="form-info">
                                                    <div className="form-info-label">Select New Asset</div>
                                                    <div className="form-info-content">
                                                        <CustomSelect
                                                            name="new_asset_id"
                                                            value={assignModalForm.new_asset_id}
                                                            options={[
                                                                { value: '', label: '-- Select --' },
                                                                ...assetAssignments
                                                                    .filter(asset => asset.asset_type?._id === assignModalForm?.asset_type && asset.assigned_to === null)
                                                                    .map(asset => ({
                                                                        value: asset._id,
                                                                        label: asset.asset_name + '(' + asset.sr_no + ')'
                                                                    }))
                                                            ]}
                                                            onChange={handleAssignChange}
                                                        />
                                                        {errorsAssignForm.new_asset_id && <div className="text-danger">{errorsAssignForm.new_asset_id}</div>}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div className="row">
                                            <div className="form-info">
                                                <div className="form-info-label">Remark</div>
                                                <div className="form-info-content">
                                                    <textarea name="remark" className="form-control" onChange={handleAssignChange} value={assignModalForm.remark}></textarea>
                                                    {errorsAssignForm.remark && <div className="text-danger">{errorsAssignForm.remark}</div>}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pp-btn-flex">
                                                <button className="btn btn-success"
                                                    onClick={handleAssignClicked} disabled={isAssigning}>
                                                    {isAssigning ? 'Saving...' : 'Save'}
                                                </button>
                                                <button className="btn btn-secondary" onClick={() => setAssignModalShow(false)}>Cancel</button>
                                            </div>
                                            {/* asset_id: '',
                                            action: '', // return, assign */}


                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}


            {/* Add/Edit Modal */}
            {modalVisible && (
                <div className="modal show d-block fade" tabIndex="-1" role="dialog" style={modalStyle} onClick={() => setModalVisible(false)}>
                    <div className="modal-dialog" role="document" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{requestFormData._id ? 'Edit Request' : 'New Request'}</h5>
                                <button type="button" className="btn-close" onClick={() => setModalVisible(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="popup-form">
                                    <div className="popup-form-row form-row-full">
                                        <div className="row">
                                            <div className="form-info">
                                                <div className="form-info-label">Request Type*</div>
                                                <div className="form-info-content">
                                                    <CustomSelect
                                                        name="request_type"
                                                        value={requestFormData.request_type}
                                                        options={[
                                                            { value: 'New', label: 'New' },
                                                            { value: 'Return', label: 'Return' },
                                                            { value: 'Replace', label: 'Replacement' },
                                                        ]}
                                                        onChange={handleChange}
                                                    />
                                                    {errorsForm.request_type && <div className="text-danger">{errorsForm.request_type}</div>}
                                                </div>
                                            </div>
                                        </div>
                                        {(requestFormData.request_type === "Return" || requestFormData.request_type === "Replace") && (
                                            <div className="row">
                                                <div className="form-info">
                                                    <div className="form-info-label">Assigned Assets*</div>
                                                    <div className="form-info-content">
                                                        <CustomSelect
                                                            name="assignment_id"
                                                            value={requestFormData.assignment_id}
                                                            options={[
                                                                { value: '', label: '-- Select --' },
                                                                ...assetAssignments.map(asset => ({
                                                                    value: asset?.assigned_to?.assignment_id || '',
                                                                    label: asset.asset_name + '(' + asset.sr_no + ')'
                                                                }))
                                                            ]}
                                                            onChange={handleChange}
                                                        />
                                                        {errorsForm.assignment_id && <div className="text-danger">{errorsForm.assignment_id}</div>}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {requestFormData.request_type === "New" && (
                                            <div className="row">
                                                <div className="form-info">
                                                    <div className="form-info-label">Asset Type*</div>
                                                    <div className="form-info-content">
                                                        <CustomSelect
                                                            name="asset_type"
                                                            value={requestFormData.asset_type}
                                                            options={[
                                                                { value: '', label: '-- Select --' },
                                                                ...assetTypes
                                                                    .filter(type => type.status === 1)
                                                                    .map(type => ({
                                                                        value: type._id,
                                                                        label: type?.type_name || '-'
                                                                    }))
                                                            ]}
                                                            onChange={handleChange}
                                                        />
                                                        {errorsForm.asset_type && <div className="text-danger">{errorsForm.asset_type}</div>}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div className="row">
                                            <div className="form-info">
                                                <div className="form-info-label">Description*</div>
                                                <div className="form-info-content">
                                                    <textarea name="request_remark" className="form-control" placeholder="Description" value={requestFormData.request_remark} onChange={handleChange}></textarea>
                                                    {errorsForm.request_remark && <div className="text-danger">{errorsForm.request_remark}</div>}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pp-btn-flex">
                                                <button type="button" className="btn btn-success"
                                                    onClick={handleSave} disabled={isAssigning}>
                                                    {isAssigning ? 'Saving...' : 'Save'}
                                                </button>
                                                <button type="button" className="btn btn-secondary" onClick={() => {
                                                    setModalVisible(false);
                                                    setModalData(null);
                                                }}>Cancel</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}


            {/* Approve/Reject Modal */}
            {ActionVisible && (
                <div className="modal show d-block fade" tabIndex="-1" role="dialog" style={modalStyle} onClick={() => setActionVisible(false)}>
                    <div className="modal-dialog" role="document" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{actionTitle}</h5>
                                <button type="button" className="btn-close" onClick={() => setActionVisible(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="popup-form">
                                    <div className="popup-form-row form-row-full">
                                        <div className="row">
                                            <div className="form-info">
                                                <div className="form-info-label">{remarkLabel}</div>
                                                <div className="form-info-content">
                                                    <textarea name="remark" className="form-control" placeholder="Remark" value={actionFormData.remark} onChange={handleChangeAction}></textarea>
                                                    {errorsForm.remark && <div className="text-danger">{errorsForm.remark}</div>}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row">
                                            {actionDeclaration && (
                                                <div className="alert alert-warning" style={{ whiteSpace: 'pre-line' }}>
                                                    <div dangerouslySetInnerHTML={{ __html: actionDeclaration }} />
                                                    <input type="checkbox" onChange={checkboxChange} /> <b><i>I hereby declare that I have read and understood the above statement.</i></b>
                                                </div>
                                            )}
                                            <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pp-btn-flex">
                                                <button type="button" className="btn btn-success"
                                                    onClick={handleActionSave} disabled={isAssigning || !enableButton}>
                                                    {isAssigning ? 'Saving...' : 'Save'}
                                                </button>
                                                <button type="button" className="btn btn-secondary" onClick={() => {
                                                    setActionVisible(false);
                                                    setActionData(null);
                                                }}>Cancel</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}


            {/* IT Procurement Cost Estimate Modal */}
            {costModalShow && (
                <div className="modal show d-block fade" tabIndex="-1" role="dialog" style={modalStyle} onClick={() => setCostModalShow(false)}>
                    <div className="modal-dialog" role="document" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{costModalForm.mode === 'financeGate' ? 'IT Procurement Review' : 'IT Procurement Action'}</h5>
                                <button type="button" className="btn-close" onClick={() => setCostModalShow(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="popup-form">
                                    <div className="popup-form-row form-row-full">
                                        <div className="row">
                                            <div className="form-info">
                                                <div className="form-info-label">Approval Flow (optional)</div>
                                                <div className="form-info-content">
                                                    {costModalForm.mode !== 'financeGate' && (
                                                        <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer', marginBottom: '6px' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={costModalForm.routing === 'business_head'}
                                                                onChange={(e) => handleCostRoutingCheckbox('business_head', e.target.checked)}
                                                            />
                                                            <span>Business Head Approval</span>
                                                        </label>
                                                    )}
                                                    <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={costModalForm.routing === 'finance_head'}
                                                            onChange={(e) => handleCostRoutingCheckbox('finance_head', e.target.checked)}
                                                        />
                                                        <span>{costModalForm.mode === 'financeGate' ? 'Send to Finance Head for approval' : 'Finance Head Approval'}</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                        {costModalForm.routing === 'finance_head' ? (
                                            <>
                                                <div className="row">
                                                    <div className="form-info">
                                                        <div className="form-info-label">Estimated Cost*</div>
                                                        <div className="form-info-content">
                                                            <input type="number" name="estimated_cost" className="form-control" value={costModalForm.estimated_cost} onChange={handleCostChange} />
                                                            {errorsCostForm.estimated_cost && <div className="text-danger">{errorsCostForm.estimated_cost}</div>}
                                                        </div>
                                                    </div>
                                                    <div className="form-info">
                                                        <div className="form-info-label">Vendor Name</div>
                                                        <div className="form-info-content">
                                                            <input type="text" name="vendor_name" className="form-control" value={costModalForm.vendor_name} onChange={handleCostChange} />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="form-info">
                                                        <div className="form-info-label">Lead Time / Delivery Timeline</div>
                                                        <div className="form-info-content">
                                                            <input type="text" name="lead_time" className="form-control" placeholder="e.g. 7 business days" value={costModalForm.lead_time} onChange={handleCostChange} />
                                                            {errorsCostForm.lead_time && <div className="text-danger">{errorsCostForm.lead_time}</div>}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="form-info">
                                                        <div className="form-info-label">Additional Notes</div>
                                                        <div className="form-info-content">
                                                            <textarea name="procurement_notes" className="form-control" value={costModalForm.procurement_notes} onChange={handleCostChange}></textarea>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        ) : costModalForm.routing === 'business_head' ? (
                                            <div className="row">
                                                <div className="form-info">
                                                    <div className="form-info-label">Attachment (optional)</div>
                                                    <div className="form-info-content">
                                                        <input type="file" className="form-control" onChange={handleCostAttachmentChange} />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="row">
                                                <div className="form-info">
                                                    <div className="form-info-label">Remark*</div>
                                                    <div className="form-info-content">
                                                        <textarea name="remark" className="form-control" placeholder="Remark" value={costModalForm.remark} onChange={handleCostChange}></textarea>
                                                        {errorsCostForm.remark && <div className="text-danger">{errorsCostForm.remark}</div>}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div className="row">
                                            <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pp-btn-flex">
                                                <button type="button" className="btn btn-success"
                                                    onClick={handleCostSave} disabled={isAssigning}>
                                                    {isAssigning ? 'Saving...' : 'Submit'}
                                                </button>
                                                <button type="button" className="btn btn-secondary" onClick={() => setCostModalShow(false)}>Cancel</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="content-block">
                <div className="filter-toolbar mt-0" id="filter">
                    <div className="row">

                        <div className="col-lg-3 col-md-4 col-sm-6 col-xs-6">
                            <div className="filter-toolbar-item">

                                <DatePicker
                                    label="From Date"
                                    value={filterForm.from_date ? parseISO(filterForm.from_date) : null}
                                    onChange={(date) =>
                                        setFilterForm({
                                            ...filterForm,
                                            from_date: date ? format(date, 'yyyy-MM-dd') : '', // LOCAL date
                                        })
                                    }
                                    format="dd MMM yyyy"
                                    slotProps={{
                                        textField: {
                                            variant: "outlined", fullWidth: true, size: "small", sx: {
                                                backgroundColor: "white",
                                                marginTop: "5px",
                                                marginBottom: "5px",
                                                height: "35px",
                                            },
                                        },
                                        popper: { placement: "top-end", modifiers: [{ name: "offset", options: { offset: [0, 8] } }] },
                                    }}
                                />

                            </div>
                        </div>

                        <div className="col-lg-3 col-md-4 col-sm-6 col-xs-6">
                            <div className="filter-toolbar-item">

                                <DatePicker
                                    label="To Date"
                                    value={filterForm.to_date ? parseISO(filterForm.to_date) : null}
                                    minDate={filterForm.from_date ? parseISO(filterForm.from_date) : null}
                                    onChange={(date) =>
                                        setFilterForm({
                                            ...filterForm,
                                            to_date: date ? format(date, 'yyyy-MM-dd') : '', // LOCAL date
                                        })
                                    }
                                    format="dd MMM yyyy"
                                    slotProps={{
                                        textField: {
                                            variant: "outlined", fullWidth: true, size: "small", sx: {
                                                backgroundColor: "white",
                                                // marginTop: "8px",
                                                // marginBottom: "8px",
                                                // height: "40px",
                                            },
                                        },
                                        popper: { placement: "top-end", modifiers: [{ name: "offset", options: { offset: [0, 8] } }] },
                                    }}
                                />

                            </div>
                        </div>

                        <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
                            <div className="filter-toolbar-item">
                                <TextField
                                    name="search_input"
                                    label="Employee Name/Code, Asset name, Remarks"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    onChange={handleChangeFilter}
                                    value={filterForm.search_input}
                                />
                            </div>
                        </div>
                        {userRole !== 7 && (    // hide from employee
                            <>
                                <div className="col-lg-3 col-md-4 col-sm-6 col-xs-6">
                                    <div className="filter-toolbar-item">
                                        <CustomSelect
                                            name="department"
                                            value={filterForm.department}
                                            options={[
                                                { value: '', label: '-- Department --' },
                                                ...departments.map(department => ({
                                                    value: department._DeptID,
                                                    label: department._DeptName
                                                }))
                                            ]}
                                            onChange={handleChangeFilter}
                                        />
                                    </div>
                                </div>
                                <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
                                    <div className="filter-toolbar-item">
                                        <CustomSelect
                                            name="rep_manager"
                                            value={filterForm.rep_manager}
                                            options={[
                                                { value: '', label: '-- Reporting Manager --' },
                                                ...managers.map(manager => ({
                                                    value: manager._UserID,
                                                    label: [
                                                        manager._FirstName,
                                                        manager._MiddleName,
                                                        manager._LastName
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' ') + ' (' + manager._UserEmployeeID + ')'
                                                }))
                                            ]}
                                            onChange={handleChangeFilter}
                                        />
                                    </div>
                                </div>
                                <div className="col-lg-3 col-md-4 col-sm-6 col-xs-6">
                                    <div className="filter-toolbar-item">
                                        <CustomSelect
                                            name="employee_status"
                                            value={filterForm.employee_status}
                                            options={[
                                                { value: '', label: '-- Employee Status --' },
                                                { value: '1', label: 'Active' },
                                                { value: '0', label: 'Inactive' },
                                            ]}
                                            onChange={handleChangeFilter}
                                        />
                                    </div>
                                </div>
                            </>
                        )}


                        <div className="col-lg-3 col-md-4 col-sm-12 col-xs-12">
                            <div className="filter-toolbar-item filter-toolbar-button">
                                <CustomSelect
                                    name="status"
                                    value={filterForm.status}
                                    options={[
                                        { value: '', label: '-- Status --' },
                                        ...statuses.map(status => ({
                                            value: String(status.value),
                                            label: status.label
                                        }))
                                    ]}
                                    onChange={handleChangeFilter}
                                />
                            </div>
                        </div>

                        <div className="col-lg-3 col-md-4 col-sm-6 col-xs-6">
                            <div className="filter-toolbar-item">
                                <CustomSelect
                                    name="asset_type"
                                    value={filterForm.asset_type}
                                    options={[
                                        { value: '', label: '-- Asset type --' },
                                        ...assetTypes.map(type => ({
                                            value: type._id,
                                            label: type.type_name
                                        }))
                                    ]}
                                    onChange={handleChangeFilter}
                                />
                            </div>
                        </div>

                        <div className="col-lg-3 col-md-4 col-sm-6 col-xs-6">
                            <div className="filter-toolbar-item">
                                <CustomSelect
                                    name="request_type"
                                    value={filterForm.request_type}
                                    options={[
                                        { value: '', label: '-- Request type --' },
                                        { value: 'New', label: 'New' },
                                        { value: 'Return', label: 'Return' },
                                        { value: 'Replace', label: 'Replacement' },
                                    ]}
                                    onChange={handleChangeFilter}
                                />
                            </div>
                        </div>
                        
                    </div>

                    {/* Filters */}
                    <div className="row">
                        <div className="col-sm-2">
                            <label>Items per page:</label>
                            <select className='form-control' onChange={(e) => setItemsPerPage(parseInt(e.target.value))}>
                                <option value="5" selected={itemsPerPage === 5}>5</option>
                                <option value="10" selected={itemsPerPage === 10}>10</option>
                                <option value="25" selected={itemsPerPage === 25}>25</option>
                                <option value="50" selected={itemsPerPage === 50}>50</option>
                                <option value="100" selected={itemsPerPage === 100}>100</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="row mt-3">
                    <div className="col-lg-12 col-md-12 col-sm-12">

                        <h2>Request
                            <span className="edit float-end">
                                <button
                                    onClick={exportToExcel}
                                    className="btn btn-success btn-sm ms-2"
                                    disabled={filteredRequests.length === 0}
                                    style={{
                                        padding: "4px 12px",
                                        fontSize: "14px",
                                        fontWeight: "500",
                                        display: "inline-flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <span
                                        className="icon"
                                        style={{
                                            fontSize: "15px",
                                            marginRight: "4px",
                                            position: "relative",
                                            top: "-1px",
                                        }}
                                    >
                                        <img
                                            src={`${process.env.REACT_APP_PUBLIC_PATH}/images/icons/icon-export.svg`}
                                            style={{ height: "18px" }}
                                            alt="Export"
                                        />
                                    </span>
                                    <span className="text">Export</span>
                                </button>
                                {userRole === 7 && (  // only employee can request
                                    <button title="Add New" className="btn add" onClick={() => { setModalData({}); setErrorsForm({}); }}>
                                        <span className="material-icons-outlined">add_box</span>
                                    </button>
                                )}
                            </span>
                        </h2>

                        <div className="table-responsive tbl-fixed-header tbl-main">
                            <table id="lettertype-table" cellpadding="0" cellspacing="0" class="table table-col-freeze">
                                <thead>
                                    <tr>
                                        {userRole !== 7 && ( <>
                                            <th>Employee</th>
                                            <th>Reporting Manager</th>
                                        </>)}
                                        <th>Request Type</th>
                                        <th>Requested At</th>
                                        <th>New Assigned Asset</th>
                                        <th>Replace/Return Asset</th>
                                        <th>Asset Type</th>
                                        <th>Remarks</th>
                                        <th>Status</th>
                                        <th class="last-col-freeze">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        currentData.length > 0 ?
                                            currentData.map((request, index) => (
                                                <tr key={index}>
                                                    {userRole !== 7 && (<>
                                                        <td>
                                                            <a
                                                                href={`../../employee/profile/${request.user_details.user_id_encrypted}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {request.user_details.fullname}<br />
                                                                [{request.user_details.department}]<br /> [{request.user_details.designation}]
                                                            </a>
                                                        </td>
                                                        <td>{request.user_details.reporting_manager_name}</td>
                                                    </>)}
                                                    <td>{request.request_type}</td>
                                                    <td>{request.created_at ? format(parseISO(request.created_at), 'dd MMM yyyy HH:mm') : '-'}</td>
                                                    <td>{request?.asset?.new
                                                        ? `${request.asset.new.asset_name} (${request.asset.new.sr_no})`
                                                        : '-'}</td>

                                                    <td>{request?.asset?.old
                                                        ? `${request.asset.old.asset_name} (${request.asset.old.sr_no})`
                                                        : '-'}</td>
                                                    <td>{request?.asset_type?.type_name || request?.asset?.old?.asset_type?.type_name || '-'}</td>

                                                    <td style={{ width: "250px" }}>
                                                        <ExpandableText text={request?.request_remark} label="Remark" />
                                                    </td>


                                                    <td>
                                                        <span className={getCurrentStatusDisplay(request).class}>
                                                            {getCurrentStatusDisplay(request).label}
                                                        </span>
                                                    </td>
                                                    <td class="last-col-freeze">
                                                        {userRole === 7 && (  // only employee can edit,cancel
                                                            <>
                                                                {[0, 10, 12].includes(request.status) && (  // 10/12 = sent back for clarification, employee must respond
                                                                    <>
                                                                        <button title="Edit" className='btn btn-sm' onClick={() => setModalData(request)}>
                                                                            <span className="material-icons-outlined">edit</span>
                                                                        </button>
                                                                        <button title="Reject" className='btn btn-sm' onClick={() => {
                                                                            setActionData({
                                                                                _id: request._id,
                                                                                status: 8,   // Cancelled by employee
                                                                                remark: ''
                                                                            });
                                                                            setActionTitle('Reject Request');
                                                                            setActionDeclaration('');
                                                                        }}>
                                                                            <span
                                                                                className="material-icons-outlined">block</span>
                                                                        </button>
                                                                    </>
                                                                )}
                                                                {request.status === 5 && ( // assigned by admin
                                                                    <>
                                                                        <button title="Acknowledge" className='btn btn-sm' onClick={() => {
                                                                            setActionData({
                                                                                _id: request._id,
                                                                                status: 6,   // Acknowledged by employee
                                                                                remark: ''
                                                                            });
                                                                            setActionTitle('Acknowledge Request');
                                                                            setActionDeclaration(declaration);
                                                                            setEnableButton(false);
                                                                        }}>
                                                                            <span
                                                                                className="material-icons-outlined"><FaCheckCircle /></span>
                                                                        </button>

                                                                        {/* <button title="Reject" className='btn btn-sm' onClick={() => {
                                                                            setActionData({
                                                                                _id: request._id,
                                                                                status: 7,   // Rejected by employee
                                                                                remark: ''
                                                                            });
                                                                            setActionTitle('Reject Assignment');
                                                                            setActionDeclaration('');
                                                                        }}>
                                                                            <span
                                                                                className="material-icons-outlined"><FaTimesCircle /></span>
                                                                        </button> */}
                                                                    </>
                                                                )}
                                                            </>
                                                        )}

                                                        {(userRole === 5 || [1, 2].includes(userRole)) && resolveAwaitingLevel(request.status, activeLevelsFor(request.asset_type), { send_to_finance: request.send_to_finance, send_to_business_head: request.send_to_business_head }) === 'reporting_manager' && (  // approve, rejection from manager, or Admin/HR override (11 = sent back to RM by Vertical Head)
                                                            <>
                                                                <button title="Approve" className='btn btn-sm' onClick={() => {
                                                                    setActionData({
                                                                        _id: request._id,
                                                                        status: 1,   // Approved by manager
                                                                        remark: ''
                                                                    });
                                                                    setActionTitle('Approve Request');
                                                                    setActionDeclaration('');
                                                                }}>
                                                                    <span
                                                                        className="material-icons-outlined">check</span>
                                                                </button>

                                                                <button title="Reject" className='btn btn-sm' onClick={() => {
                                                                    setActionData({
                                                                        _id: request._id,
                                                                        status: 2,   // Rejected by manager
                                                                        remark: ''
                                                                    });
                                                                    setActionTitle('Reject Request');
                                                                    setActionDeclaration('');
                                                                }}>
                                                                    <span
                                                                        className="material-icons-outlined">block</span>
                                                                </button>

                                                                {request.status === 0 && (
                                                                    <button title="Send Back to Employee for Clarification" className='btn btn-sm' onClick={() => {
                                                                        setActionData({
                                                                            _id: request._id,
                                                                            status: 10,   // Sent back by Reporting Manager for clarification
                                                                            remark: ''
                                                                        });
                                                                        setActionTitle('Send Back for Clarification');
                                                                        setActionDeclaration('');
                                                                    }}>
                                                                        <span
                                                                            className="material-icons-outlined">reply</span>
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}

                                                        {(([1, 2].includes(userRole)) || (loggedInUser?.id != null && Number(loggedInUser.id) === Number(request.user_details?.vertical_head_id))) && (  // Vertical Head, or Admin/HR override
                                                            <>
                                                                {resolveAwaitingLevel(request.status, activeLevelsFor(request.asset_type), { send_to_finance: request.send_to_finance, send_to_business_head: request.send_to_business_head }) === 'vertical_head' && (
                                                                    <>
                                                                        <button title="Approve" className='btn btn-sm' onClick={() => {
                                                                            setActionData({ _id: request._id, status: 14, remark: '' }); // Approved by Vertical Head
                                                                            setActionTitle('Approve Request');
                                                                            setActionDeclaration('');
                                                                        }}>
                                                                            <span className="material-icons-outlined">check</span>
                                                                        </button>
                                                                        <button title="Reject" className='btn btn-sm' onClick={() => {
                                                                            setActionData({ _id: request._id, status: 15, remark: '' }); // Rejected by Vertical Head
                                                                            setActionTitle('Reject Request');
                                                                            setActionDeclaration('');
                                                                        }}>
                                                                            <span className="material-icons-outlined">block</span>
                                                                        </button>
                                                                        {activeLevelsFor(request.asset_type).includes('reporting_manager') && (
                                                                            <button title="Send Back to Reporting Manager" className='btn btn-sm' onClick={() => {
                                                                                setActionData({ _id: request._id, status: 11, remark: '' }); // Sent back by Vertical Head
                                                                                setActionTitle('Send Back to Reporting Manager');
                                                                                setActionDeclaration('');
                                                                            }}>
                                                                                <span className="material-icons-outlined">reply</span>
                                                                            </button>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </>
                                                        )}

                                                        {(userRole === 9 || [1, 2].includes(userRole)) && (  // IT Procurement, or Admin/HR override
                                                            <>
                                                                {resolveAwaitingLevel(request.status, activeLevelsFor(request.asset_type), { send_to_finance: request.send_to_finance, send_to_business_head: request.send_to_business_head }) === 'it_procurement' && (
                                                                    request.status === LEVEL_STATUS.finance_head.approve ? (
                                                                        // Final checkpoint: Finance Head already approved — IT gives the last approve/reject.
                                                                        <>
                                                                            <button title="Approve" className='btn btn-sm' onClick={() => {
                                                                                setActionData({ _id: request._id, status: IT_FINAL_REVIEW_STATUS.approve, remark: '' });
                                                                                setActionTitle('Approve Request');
                                                                                setActionDeclaration('');
                                                                            }}>
                                                                                <span className="material-icons-outlined">check</span>
                                                                            </button>
                                                                            <button title="Reject" className='btn btn-sm' onClick={() => {
                                                                                setActionData({ _id: request._id, status: IT_FINAL_REVIEW_STATUS.reject, remark: '' });
                                                                                setActionTitle('Reject Request');
                                                                                setActionDeclaration('');
                                                                            }}>
                                                                                <span className="material-icons-outlined">block</span>
                                                                            </button>
                                                                        </>
                                                                    ) : request.status === LEVEL_STATUS.business_head.approve ? (
                                                                        // Second checkpoint: Business Head already approved — IT decides whether this
                                                                        // also needs Finance Head, via the same form used for the first submission.
                                                                        <button title="Review" className='btn btn-sm' onClick={() => {
                                                                            setCostModalForm({
                                                                                _id: request._id,
                                                                                mode: 'financeGate',
                                                                                routing: 'none',
                                                                                estimated_cost: request.estimated_cost || '',
                                                                                vendor_name: request.vendor_name || '',
                                                                                lead_time: request.lead_time || '',
                                                                                procurement_notes: request.procurement_notes || '',
                                                                                remark: '',
                                                                                attachment: null,
                                                                            });
                                                                            setErrorsCostForm({});
                                                                            setCostModalShow(true);
                                                                        }}>
                                                                            <span className="material-icons-outlined">check</span>
                                                                        </button>
                                                                    ) : (
                                                                        <>
                                                                            <button title="Submit Cost Estimate" className='btn btn-sm' onClick={() => {
                                                                                setCostModalForm({
                                                                                    _id: request._id,
                                                                                    mode: 'submit',
                                                                                    routing: routingFor(request),
                                                                                    estimated_cost: request.estimated_cost || '',
                                                                                    vendor_name: request.vendor_name || '',
                                                                                    lead_time: request.lead_time || '',
                                                                                    procurement_notes: request.procurement_notes || '',
                                                                                    remark: '',
                                                                                    attachment: null,
                                                                                });
                                                                                setErrorsCostForm({});
                                                                                setCostModalShow(true);
                                                                            }}>
                                                                                <span className="material-icons-outlined">check</span>
                                                                            </button>
                                                                            <button title="Reject" className='btn btn-sm' onClick={() => {
                                                                                setActionData({ _id: request._id, status: 17, remark: '' }); // Rejected by IT Procurement
                                                                                setActionTitle('Reject Request');
                                                                                setActionDeclaration('');
                                                                            }}>
                                                                                <span className="material-icons-outlined">block</span>
                                                                            </button>
                                                                        </>
                                                                    )
                                                                )}
                                                            </>
                                                        )}

                                                        {(([1, 2].includes(userRole)) || (loggedInUser?.id != null && Number(loggedInUser.id) === Number(request.user_details?.business_head_id))) && (  // Business Head, or Admin/HR override
                                                            <>
                                                                {resolveAwaitingLevel(request.status, activeLevelsFor(request.asset_type), { send_to_finance: request.send_to_finance, send_to_business_head: request.send_to_business_head }) === 'business_head' && (
                                                                    <>
                                                                        <button title="Approve" className='btn btn-sm' onClick={() => {
                                                                            setActionData({ _id: request._id, status: 20, remark: '' }); // Approved by Business Head
                                                                            setActionTitle('Approve Request');
                                                                            setActionDeclaration('');
                                                                        }}>
                                                                            <span className="material-icons-outlined">check</span>
                                                                        </button>
                                                                        <button title="Reject" className='btn btn-sm' onClick={() => {
                                                                            setActionData({ _id: request._id, status: 21, remark: '' }); // Rejected by Business Head
                                                                            setActionTitle('Reject Request');
                                                                            setActionDeclaration('');
                                                                        }}>
                                                                            <span className="material-icons-outlined">block</span>
                                                                        </button>
                                                                        <button title="Send Back to IT for Revised Details" className='btn btn-sm' onClick={() => {
                                                                            setActionData({ _id: request._id, status: 22, remark: '' }); // Sent back by Business Head
                                                                            setActionTitle('Send Back for Revised Details');
                                                                            setActionDeclaration('');
                                                                        }}>
                                                                            <span className="material-icons-outlined">reply</span>
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </>
                                                        )}

                                                        {(userRole === 4 || [1, 2].includes(userRole)) && (  // Finance Head, or Admin/HR override
                                                            <>
                                                                {resolveAwaitingLevel(request.status, activeLevelsFor(request.asset_type), { send_to_finance: request.send_to_finance, send_to_business_head: request.send_to_business_head }) === 'finance_head' && (
                                                                    <>
                                                                        <button title="Approve" className='btn btn-sm' onClick={() => {
                                                                            setActionData({ _id: request._id, status: 18, remark: '' }); // Approved by Finance Head
                                                                            setActionTitle('Approve Request');
                                                                            setActionDeclaration('');
                                                                        }}>
                                                                            <span className="material-icons-outlined">check</span>
                                                                        </button>
                                                                        <button title="Reject" className='btn btn-sm' onClick={() => {
                                                                            setActionData({ _id: request._id, status: 19, remark: '' }); // Rejected by Finance Head
                                                                            setActionTitle('Reject Request');
                                                                            setActionDeclaration('');
                                                                        }}>
                                                                            <span className="material-icons-outlined">block</span>
                                                                        </button>
                                                                        <button title="Send Back to IT for Revised Costing" className='btn btn-sm' onClick={() => {
                                                                            setActionData({ _id: request._id, status: 13, remark: '' }); // Sent back by Finance Head
                                                                            setActionTitle('Send Back for Revised Costing');
                                                                            setActionDeclaration('');
                                                                        }}>
                                                                            <span className="material-icons-outlined">reply</span>
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </>
                                                        )}

                                                        {[1, 2, 8, 9].includes(userRole) && (  // approve, rejection from admin
                                                            <>
                                                                {activeLevelsFor(request.asset_type).length === 0 && ([1, 2].includes(userRole) || ([8, 9].includes(userRole) && request.status === 1)) && (  // legacy 2-level flow only (no reporting_manager/vertical_head/it_procurement configured); Admin/HR act at any stage, IT-Admin once manager has approved
                                                                    <>
                                                                        {[0, 1].includes(request.status) && ( //  request.status === 1 if approved by manager
                                                                            <button title="Approve" className='btn btn-sm' onClick={() => {
                                                                                setActionData({
                                                                                    _id: request._id,
                                                                                    status: 3,   // Approved by admin
                                                                                    remark: ''
                                                                                });
                                                                                setActionTitle('Approve Request');
                                                                                setActionDeclaration('');
                                                                            }}>
                                                                                <span
                                                                                    className="material-icons-outlined">check</span>
                                                                            </button>
                                                                        )}

                                                                        {([0, 1, 3].includes(request.status)) && (    // request.status === 1 || request.status === 3 if approved by manager or admin
                                                                            <button title="Reject" className='btn btn-sm' onClick={() => {
                                                                                setActionData({
                                                                                    _id: request._id,
                                                                                    status: 4,   // Rejected by admin
                                                                                    remark: ''
                                                                                });
                                                                                setActionTitle('Reject Request');
                                                                                setActionDeclaration('');
                                                                            }}>
                                                                                <span
                                                                                    className="material-icons-outlined">block</span>
                                                                            </button>
                                                                        )}
                                                                    </>
                                                                )}

                                                                {request.awaiting_assignment && (  // Fully approved (legacy admin, or terminal level of the configured flow), ready to assign
                                                                    <button
                                                                        title="Assign/Return/Replace"
                                                                        className="btn"
                                                                        onClick={() => {
                                                                            setAssignModalShow(true);
                                                                            const temp = {
                                                                                request_id: request._id,
                                                                                asset_type: request.request_type === "New" ? request?.asset_type?._id : request?.asset?.old?.asset_type?._id || null,
                                                                                user_id: request.user_id,
                                                                                request_type: request.request_type,
                                                                                asset_id: request.request_type === "New" ? null : request?.asset?.old?._id || null,
                                                                                remark: '',
                                                                            };
                                                                            setAssignModalForm(temp);

                                                                            // console.log('request', request);
                                                                            // console.log('temp', temp); // for debugging, can be removed in production
                                                                        }}
                                                                    >
                                                                        <span
                                                                            className="material-icons-outlined">assignment_turned_in</span>
                                                                    </button>
                                                                )}

                                                            </>
                                                        )}

                                                        <button title="Details" className="btn" onClick={() => navigate(`/asset-management/request-details/${request._id}`)}>
                                                            <span className="material-icons-outlined">remove_red_eye</span>
                                                        </button>



                                                    </td>

                                                </tr>
                                            ))
                                            :
                                            <p className="text-center">No data found.</p>
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Pagination */}
                {/* <div class="row align-items-center">
                    <div class="col-sm-6 text-start">
                        {
                            totalPages > 1 && (
                                <nav aria-label="Page navigation">
                                    <ul class="pagination">
                                        {Array.from({ length: totalPages }, (_, index) => (
                                            <li key={index} className={`${currentPage === index + 1 ? 'active' : ''}`}>
                                                <button className="btn btn-sm" onClick={() => paginate(index + 1)}>{index + 1}</button>
                                            </li>
                                        ))}
                                    </ul>
                                </nav>
                            )
                        }
                    </div>
                    <div class="col-sm-6 text-end">
                        Showing {filteredRequests.length > indexOfFirstItem ? indexOfFirstItem + 1 : 0} to {filteredRequests.length > indexOfLastItem ? indexOfLastItem : filteredRequests.length} of {filteredRequests.length} ({totalPages} Pages)
                    </div>
                </div> */}
                <div className="row align-items-center">
                    <div className="col-sm-6 text-start">
                        {totalPages > 1 && (
                            <nav aria-label="Page navigation">
                                <ul className="pagination">

                                    {/* First */}
                                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                        <button className="page-link btn btn-sm" onClick={() => paginate(1)}>
                                            ≪
                                        </button>
                                    </li>

                                    {/* Previous */}
                                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                        <button className="page-link btn btn-sm" onClick={() => paginate(currentPage - 1)}>
                                            ‹
                                        </button>
                                    </li>

                                    {/* Limited Page Numbers */}
                                    {(() => {
                                        const pageNumbers = [];
                                        const maxVisible = 3; // only 3 pages show
                                        let start = Math.max(1, currentPage - 1);
                                        let end = Math.min(totalPages, start + maxVisible - 1);

                                        // adjust start if pages fewer
                                        if (end - start < maxVisible - 1) {
                                            start = Math.max(1, end - maxVisible + 1);
                                        }

                                        for (let i = start; i <= end; i++) {
                                            pageNumbers.push(
                                                <li
                                                    key={i}
                                                    className={`page-item ${currentPage === i ? "active" : ""}`}
                                                >
                                                    <button className="page-link btn btn-sm" onClick={() => paginate(i)}>
                                                        {i}
                                                    </button>
                                                </li>
                                            );
                                        }

                                        return pageNumbers;
                                    })()}

                                    {/* Next */}
                                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                        <button className="page-link btn btn-sm" onClick={() => paginate(currentPage + 1)}>
                                            ›
                                        </button>
                                    </li>

                                    {/* Last */}
                                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                        <button className="page-link btn btn-sm" onClick={() => paginate(totalPages)}>
                                            ≫
                                        </button>
                                    </li>

                                </ul>
                            </nav>
                        )}
                    </div>

                    <div className="col-sm-6 text-end">
                        Showing {filteredRequests.length > indexOfFirstItem ? indexOfFirstItem + 1 : 0}
                        {" "}to{" "}
                        {filteredRequests.length > indexOfLastItem ? indexOfLastItem : filteredRequests.length}
                        {" "}of {filteredRequests.length} ({totalPages} Pages)
                    </div>
                </div>

            </div >
        </div >
    );
}

export default RequestTab;